import './mines-grid.scss';

import {
  Fit,
  Layout,
  useRive,
  useViewModel,
  useViewModelInstance,
  useViewModelInstanceTrigger,
} from '@rive-app/react-webgl2';
import { Bomb, Gem } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  minesActions,
  selectMinesGame,
  selectMinesPendingCell,
  selectMinesRadarHint,
  selectMinesResult,
  selectMinesStatus,
} from '@/entities/mines';
import { toastService } from '@/shared/lib/toast/toastService';

const TOTAL_CELLS = 25;

export function MinesGrid() {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectMinesStatus);
  const game = useAppSelector(selectMinesGame);
  const result = useAppSelector(selectMinesResult);
  const pendingCell = useAppSelector(selectMinesPendingCell);

  const radarHint = useAppSelector(selectMinesRadarHint);

  const { rive, RiveComponent } = useRive({
    src: '/assets/mines/win_animation.riv',
    stateMachines: 'State Machine 1',
    autoplay: true,
    layout: new Layout({ fit: Fit.Cover }),
  });

  const viewModel = useViewModel(rive);
  const vmInstance = useViewModelInstance(viewModel, { rive });

  const { trigger: triggerLightEffect } = useViewModelInstanceTrigger(
    'trigger_light_effect',
    vmInstance,
  );

  const hasTriggeredRadar = useRef(false);

  useEffect(() => {
    if (status === 'idle') hasTriggeredRadar.current = false;
  }, [status]);

  useEffect(() => {
    if (!radarHint || !vmInstance || hasTriggeredRadar.current) return;
    // Skip if animation already played for this game (survives reload)
    if (localStorage.getItem('mines_radar_animated') === game?.gameId) return;
    hasTriggeredRadar.current = true;
    triggerLightEffect();
    toastService.minesRadarHint();
    if (game?.gameId) localStorage.setItem('mines_radar_animated', game.gameId);
  }, [radarHint, vmInstance]);

  const revealed = game?.revealedPositions ?? [];
  const mines = result?.minePositions ?? [];
  const isPlaying = status === 'playing';
  const isOver = status === 'busted' || status === 'won';

  const handleClick = (index: number) => {
    if (!isPlaying || pendingCell !== null || revealed.includes(index)) return;
    dispatch(minesActions.clickCell(index));
  };

  return (
    <div className="mines-grid__wrap">
      <RiveComponent className="mines-grid__rive" />
      <div className="mines-grid">
        {Array.from({ length: TOTAL_CELLS }, (_, i) => {
          const isSafe = revealed.includes(i);
          const isMine = isOver && mines.includes(i);
          const isPending = pendingCell === i;
          const isClickable = isPlaying && !isSafe && pendingCell === null;
          const isRadar = radarHint === i && isPlaying && !isSafe;

          return (
            <button
              key={i}
              className={[
                'mines-grid__cell',
                isSafe && 'mines-grid__cell--safe',
                isMine && 'mines-grid__cell--mine',
                isPending && 'mines-grid__cell--pending',
                isClickable && 'mines-grid__cell--clickable',
                isRadar && 'mines-grid__cell--radar',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleClick(i)}
              disabled={!isClickable}>
              {isSafe && <Gem size={22} className="mines-grid__icon mines-grid__icon--gem" />}
              {isMine && <Bomb size={22} className="mines-grid__icon mines-grid__icon--bomb" />}
              {isRadar && (
                <Gem size={22} className="mines-grid__icon mines-grid__icon--radar-gem" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
